import type { useLoggedInUser } from "Hooks/useLoggedInUser";
import { SignUpRoleIntent, UserStatus } from "Infrastructure/Api/Api";
import { LocalStorageKeys } from "Utils/Storage/LocalStorage/LocalStorageKeys";
import { getLocalStorageItem } from "Utils/Storage/LocalStorage/SecureLocalStorage";

type LoggedInUser = ReturnType<typeof useLoggedInUser>;

export type AppleSsoContext = {
  affiliateCode: string | undefined;
  anonymousLogin: string | undefined;
  roleIntent: SignUpRoleIntent | undefined;
};

// Sign-up context that rides along with an Apple authorization code: the affiliate who
// referred the person, the role they came to register for, and the guest account to
// convert. Shared by AppleButton (popup + native iOS, which have the code in hand) and
// AppleRedirectPage (the Android browser round trip, which reloads the page before the
// code arrives) so both paths resolve it identically from the same stored values.
export const getAppleSsoContext = (
  user: LoggedInUser,
  affiliateCodeProp?: string,
): AppleSsoContext => {
  const affiliateCode =
    affiliateCodeProp ||
    getLocalStorageItem(LocalStorageKeys.AffiliateCode) ||
    undefined;

  // Role registration links are Customer-first — the wanted role rides along as an intent.
  // Validated against the known intents so garbage in storage is never forwarded.
  const storedRoleIntent = getLocalStorageItem(LocalStorageKeys.RoleIntent);
  const roleIntent =
    storedRoleIntent &&
    Object.values(SignUpRoleIntent).includes(
      storedRoleIntent as SignUpRoleIntent,
    )
      ? (storedRoleIntent as SignUpRoleIntent)
      : undefined;

  const anonymousLogin =
    user?.userStatus === UserStatus.Anonymous ? user?.login : undefined;

  return { affiliateCode, anonymousLogin, roleIntent };
};
